// app/location-map.web.tsx
import React, { useMemo } from "react";
import { router, useLocalSearchParams } from "expo-router";

function parseNumber(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function readParam(value: string | string[] | undefined) {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === "string" && raw.trim() ? raw.trim() : "";
}

export default function LocationMapWebScreen() {
  const params = useLocalSearchParams();

  const lat = parseNumber(params.lat);
  const lng = parseNumber(params.lng);

  const title =
    readParam(params.title) ||
    readParam(params.businessName) ||
    readParam(params.name) ||
    "Business location";

  const addressLine =
    readParam(params.addressLine) ||
    readParam(params.address) ||
    readParam(params.formattedAddress);

  const cityLine = readParam(params.cityLine);
  const queryParam = readParam(params.query);

  const mapQuery = useMemo(() => {
    if (lat != null && lng != null) return `${lat},${lng}`;

    return (
      queryParam ||
      [addressLine, cityLine, title].filter(Boolean).join(", ")
    );
  }, [lat, lng, queryParam, addressLine, cityLine, title]);

  const embedUrl = mapQuery
    ? `https://www.google.com/maps?q=${encodeURIComponent(
        mapQuery
      )}&z=16&output=embed`
    : "";

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => router.back()}>
          ← Back
        </button>

        <div style={styles.titleWrap}>
          <div style={styles.title}>{title}</div>
          {addressLine ? <div style={styles.subtitle}>{addressLine}</div> : null}
        </div>
      </div>

      {embedUrl ? (
        <iframe
          title="Google Maps"
          src={embedUrl}
          style={styles.iframe}
          allow="geolocation; fullscreen"
          loading="lazy"
        />
      ) : (
        <div style={styles.empty}>No location found.</div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
  },

  header: {
    minHeight: 74,
    padding: "12px 18px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: 12,
    boxSizing: "border-box",
  },

  backButton: {
    border: 0,
    background: "transparent",
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
    cursor: "pointer",
  },

  titleWrap: {
    minWidth: 0,
  },

  title: {
    fontSize: 16,
    fontWeight: 900,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  subtitle: {
    marginTop: 2,
    fontSize: 12,
    fontWeight: 600,
    color: "#64748b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  iframe: {
    flex: 1,
    width: "100%",
    border: 0,
  },

  empty: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontWeight: 700,
  },
};